/**
 * Occurrence expansion for the portal-native scheduling docs (`timeSlots` +
 * `events`) — THE single engine that turns a recurring weekly doc
 * (isRecurring + dayOfWeek + startDate/endDate) or a single-date doc
 * (specificDate) into dated occurrences, honoring the embedded `exceptions`
 * map ({ 'YYYY-MM-DD' → patch | { cancelled:true } }, with an optional
 * patch.date meaning a cross-day move).
 *
 * Extracted verbatim from the Calendar tab (Slice B) so the customer schedule
 * view reads lessons through the exact same semantics staff see. Do not write
 * a second expander — import this one.
 *
 * Studio closures block here: a date in the season canon's CLOSED_DATES
 * (holidays, breaks, recital days) yields NO occurrence, so a recurring weekly
 * lesson silently stops rendering on every closed day for staff and customers
 * at once.
 *
 * Flex/reserve days block RECURRING lessons only. They are checked on the
 * recurring leg of `naturallyHere` rather than at the top of expandOnto, and
 * that placement is deliberate: the early return is shared by the single-date
 * branch — which is the shape a MAKEUP slot has — and by the moved-into loop.
 * Blocking there would stop makeups landing on flex days, which is the one
 * thing flex days exist for. See src/data/seasonData.js for the two sets.
 */

import { isClosedDate, isNoLessonDate } from '../data/seasonData';

export const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

// Builds lesson occurrence objects with the per-date override applied. Each
// carries _slotId + _originDate (the exception key) + _date (the rendered date).
export const lessonOccurrence = (slot, originStr, displayed, patch) => ({
	...slot,
	_slotId: slot.id,
	_kind: 'lesson',
	_originDate: originStr,
	_date: new Date(displayed),
	startTime: (patch && patch.startTime) || slot.startTime,
	endTime: (patch && patch.endTime) || slot.endTime,
	instructor: patch && patch.instructor != null ? patch.instructor : slot.instructor,
	instrument: (patch && patch.instrument) || slot.instrument,
	location: (patch && patch.location) || slot.location,
});

// Event occurrence — same occurrence semantics as timeSlots, but typed (the
// `_kind` is the event type, used for color + drawer branching). `exceptions`
// is honored on read for forward-compat, though v1 UI never writes one.
export const eventOccurrence = (ev, originStr, displayed, patch) => ({
	...ev,
	_eventId: ev.id,
	_kind: ev.type,
	_originDate: originStr,
	_date: new Date(displayed),
	startTime: (patch && patch.startTime) || ev.startTime,
	endTime: (patch && patch.endTime) || ev.endTime,
	location: patch && patch.location !== undefined ? patch.location : ev.location,
});

// Shared occurrence expansion (recurring weekly vs single specificDate, with
// the exception map applied) for both timeSlots and events.
export const expandOnto = (record, date, dateStr, dow, builder, out) => {
	// Both canon suppressions below apply to LESSONS ONLY. An `events` doc is a
	// thing staff deliberately placed on a specific date, and on a closed day it
	// is usually the REASON the day is closed.
	//
	// FOUND 2026-08-06, on the first real recital event anyone created: a recital
	// date is a CLOSED date in the season canon, so the early return deleted the
	// recital event from its own date. It rendered in no view, and because the
	// Calendar can only select what it renders, the event could not be opened,
	// edited or deleted through the UI at all — it existed only in Firestore.
	//
	// Keyed off the BUILDER rather than a caller-supplied flag: both builders
	// live in this module, so a caller cannot forget to pass it and cannot get it
	// wrong. Do not "simplify" this into an option argument.
	const isEvent = builder === eventOccurrence;

	// Studio dark on this date → no LESSON occurrence at all (natural or
	// moved-into). Canon-driven; see src/data/seasonData.js CLOSED_DATES.
	if (!isEvent && isClosedDate(dateStr)) return;
	const exc = record.exceptions || {};
	// A recurring weekly lesson also stops on flex/reserve days (isNoLessonDate).
	// A single-date record — i.e. a makeup slot — deliberately does NOT: placing
	// makeups on flex days is what the days are held back for. A recurring EVENT
	// does not stop either: a flex day holds back automatic lesson expansion, not
	// a group class someone scheduled on purpose.
	const naturallyHere = record.isRecurring
		? (record.dayOfWeek === dow
			&& (isEvent || !isNoLessonDate(dateStr))
			&& !(record.startDate && dateStr < record.startDate)
			&& !(record.endDate && dateStr > record.endDate))
		: record.specificDate === dateStr;
	if (naturallyHere) {
		const e = exc[dateStr];
		if (!(e && e.cancelled) && !(e && e.date && e.date !== dateStr)) {
			out.push(builder(record, dateStr, date, e));
		}
	}
	// occurrences moved INTO this date from another origin date
	Object.keys(exc).forEach((k) => {
		const e = exc[k];
		if (e && !e.cancelled && e.date === dateStr && k !== dateStr) {
			out.push(builder(record, k, date, e));
		}
	});
};

// Walks day-by-day from `from` for `days` days and returns every lesson
// occurrence of `slots`, sorted by date then start time. The customer
// schedule view's read shape; the Calendar tab drives expandOnto directly
// (per rendered grid cell) instead.
export const expandUpcomingLessons = (slots, from, days) => {
	const out = [];
	for (let i = 0; i < days; i += 1) {
		const date = new Date(from);
		date.setDate(date.getDate() + i);
		const dateStr = ymd(date);
		const dow = date.getDay();
		slots.forEach((slot) => expandOnto(slot, date, dateStr, dow, lessonOccurrence, out));
	}
	return out.sort((a, b) =>
		ymd(a._date) === ymd(b._date)
			? (a.startTime || '').localeCompare(b.startTime || '')
			: ymd(a._date).localeCompare(ymd(b._date))
	);
};
